import React, { useEffect, useMemo, useState } from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import birthChartCityCatalog from "./cityCatalog";
import {
  getBirthChartCities,
  getBirthChartNaming,
} from "../../services/api/http";

const PRIORITY_FIELDS = [
  "recommended_syllable",
  "syllables_for_nakshatra",
  "nakshatra_name",
  "nakshatra_number",
  "pada",
  "resolved_city",
  "resolved_timezone_name",
  "birth_datetime",
  "timezone",
  "latitude",
  "longitude",
  "julian_day_ut",
  "moon_nirayana_longitude",
  "note",
];

const formatLabel = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const formatValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  if (typeof value === "number") {
    return Number.isInteger(value) ? value : value.toFixed(4);
  }
  if (value === null || value === undefined || value === "") {
    return "N/A";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
};

const getCityOptionValue = (city) => `${city.city}||${city.timezone_name}`;

export default function BirthChartNamesPage() {
  const [formData, setFormData] = useState({
    birthDatetime: "",
    city: "",
    country: "",
  });
  const [result, setResult] = useState(null);
  const [matches, setMatches] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCityLoading, setIsCityLoading] = useState(false);
  const [countries, setCountries] = useState(birthChartCityCatalog.countries);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadCountries = async () => {
      try {
        const response = await getBirthChartCities();
        if (!isMounted) {
          return;
        }
        setCountries(response.data?.countries?.length ? response.data.countries : birthChartCityCatalog.countries);
      } catch (requestError) {
        if (!isMounted) {
          return;
        }
        setCountries(birthChartCityCatalog.countries);
      }
    };

    loadCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadCities = async () => {
      if (!formData.country) {
        setCities([]);
        return;
      }

      setIsCityLoading(true);
      try {
        const response = await getBirthChartCities(formData.country);
        if (!isMounted) {
          return;
        }
        const responseCities = response.data?.cities || [];
        const fallbackCities = birthChartCityCatalog.citiesByCountry[formData.country] || [];
        setCities(responseCities.length ? responseCities : fallbackCities);
        if (!responseCities.length && !fallbackCities.length) {
          setError("No cities were found for the selected country.");
        }
      } catch (requestError) {
        if (!isMounted) {
          return;
        }
        const fallbackCities = birthChartCityCatalog.citiesByCountry[formData.country] || [];
        setCities(fallbackCities);
        if (!fallbackCities.length) {
          setError(
            requestError?.response?.data?.error ||
              "Unable to load cities for the selected country."
          );
        }
      } finally {
        if (isMounted) {
          setIsCityLoading(false);
        }
      }
    };

    loadCities();

    return () => {
      isMounted = false;
    };
  }, [formData.country]);

  const orderedEntries = useMemo(() => {
    if (!result) {
      return [];
    }

    const remainingEntries = Object.entries(result).filter(
      ([key]) => !PRIORITY_FIELDS.includes(key)
    );

    const priorityEntries = PRIORITY_FIELDS.filter((key) => key in result).map(
      (key) => [key, result[key]]
    );

    return [...priorityEntries, ...remainingEntries];
  }, [result]);

  const selectedCity = useMemo(
    () => cities.find((city) => getCityOptionValue(city) === formData.city) || null,
    [cities, formData.city]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setError("");

    if (name === "country") {
      setFormData((current) => ({
        ...current,
        country: value,
        city: "",
      }));
      return;
    }

    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const getMissingFieldError = () => {
    if (!formData.birthDatetime) {
      return "Birth date and time is required.";
    }
    if (!formData.country) {
      return "Country is required.";
    }
    if (!formData.city || !selectedCity) {
      return "City is required.";
    }
    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMatches([]);

    const missingFieldError = getMissingFieldError();
    if (missingFieldError) {
      setResult(null);
      setError(missingFieldError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await getBirthChartNaming({
        birth_datetime: formData.birthDatetime,
        city: selectedCity?.city || "",
        country: formData.country,
      });

      setResult(response.data);
    } catch (requestError) {
      setResult(null);
      const responseData = requestError?.response?.data || {};
      setError(responseData.error || "Unable to calculate birth chart naming data.");
      setMatches(responseData.matches || []);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SiteLayout>
      <main className="page-shell home-v3-shell">
        <PageIntro
          eyebrow="Spirituality / Birth Chart Naming"
          title="Find the right naming syllable."
          description="Choose a country and city, enter the birth time, and calculate nakshatra, pada, the recommended syllable, and chart data."
        />

          <section className="section">
            <div className="row g-4 align-items-start">
              <div className="col-lg-5">
                <div className="home-v3-panel card-pad">
                  <span className="home-v3-kicker">Input</span>
                  <h2>Birth details I enter.</h2>
                  <p>
                    I enter the date, time, country, and city to resolve the
                    birth chart naming data against the selected location.
                  </p>
                  <form className="birth-chart-form" onSubmit={handleSubmit}>
                  <label className="form-field">
                    <span>Birth date and time</span>
                    <input
                      type="datetime-local"
                      name="birthDatetime"
                      value={formData.birthDatetime}
                      onChange={handleChange}
                      className="form-control modern-input"
                      required
                    />
                  </label>
                  <label className="form-field">
                    <span>Country</span>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="form-control modern-input"
                      required
                    >
                      <option value="">
                        Select a country
                      </option>
                      {countries.map((country) => (
                        <option value={country} key={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="form-field">
                    <span>City</span>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="form-control modern-input"
                      required
                      disabled={!formData.country || isCityLoading}
                    >
                      <option value="">
                        {!formData.country
                          ? "Select a country first"
                          : isCityLoading
                            ? "Loading cities..."
                            : "Select a city"}
                      </option>
                      {cities.map((city) => (
                        <option value={getCityOptionValue(city)} key={getCityOptionValue(city)}>
                          {city.city}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="submit"
                    className="button-primary birth-chart-submit"
                    disabled={isLoading || !selectedCity}
                  >
                    {isLoading ? "Calculating..." : "Get Name Suggestions"}
                  </button>
                </form>
                {error ? <p className="birth-chart-error">{error}</p> : null}
                {matches.length ? (
                  <div className="birth-chart-suggestions">
                    <span className="eyebrow">Possible Matches</span>
                    <div className="pill-list">
                      {matches.map((match) => (
                        <span
                          className="pill"
                          key={`${match.city}-${match.country}-${match.timezone_name}`}
                        >
                          {match.city}
                          {match.country ? `, ${match.country}` : ""}
                          {" · "}
                          {match.timezone_name}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="col-lg-7">
              <div className="home-v3-panel card-pad">
                <span className="home-v3-kicker">Result</span>
                <h2>Name guidance I use.</h2>
                <p>
                  My result card shows the recommended syllable first, then the
                  remaining chart metadata returned by the API.
                </p>
                {!result ? (
                  <p>
                    I submit the form to generate the recommended starting sound
                    and the rest of the returned chart metadata.
                  </p>
                ) : (
                  <>
                    <div className="birth-chart-highlight-grid">
                      <div className="birth-chart-highlight">
                        <span className="stat-card-label">Recommended syllable</span>
                        <span className="birth-chart-highlight-value">
                          {formatValue(result.recommended_syllable)}
                        </span>
                      </div>
                      <div className="birth-chart-highlight">
                        <span className="stat-card-label">Nakshatra</span>
                        <span className="birth-chart-highlight-value">
                          {formatValue(result.nakshatra_name)}
                        </span>
                      </div>
                      <div className="birth-chart-highlight">
                        <span className="stat-card-label">Pada</span>
                        <span className="birth-chart-highlight-value">
                          {formatValue(result.pada)}
                        </span>
                      </div>
                    </div>

                    <div className="birth-chart-detail-list">
                      {orderedEntries.map(([key, value]) => (
                        <div className="birth-chart-detail-row" key={key}>
                          <span className="birth-chart-detail-label">
                            {formatLabel(key)}
                          </span>
                          <span className="birth-chart-detail-value">
                            {formatValue(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}
